/**
 * Vincent #275/#291 (2026-06-11) — Modale d'effacement de vidéo avec liste à
 * cocher des connaissances à CONSERVER.
 *
 * Remplace l'ancien enchaînement window.confirm/window.prompt (rejeté au QA).
 * Workflow :
 *   1. Charge GET /api/videos/{id}/cascade-preview → comptes réels par type
 *   2. Affiche la liste à cocher (coché = CONSERVÉ, décoché = effacé)
 *   3. Exige de taper "effacement" pour activer le bouton final
 *   4. DELETE /api/videos/{id}?keep=qa,script,... → toast récapitulatif
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash, Warning, X, SpinnerGap } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const KEEP_TYPES = [
  { key: "qa", labelFr: "Q&R / FAQ", labelEn: "Q&A / FAQ" },
  { key: "script", labelFr: "Scripts de vente", labelEn: "Sales scripts" },
  { key: "procedure", labelFr: "Procédures", labelEn: "Procédures" },
  { key: "concept", labelFr: "Concepts", labelEn: "Concepts" },
  { key: "objection", labelFr: "Objections", labelEn: "Objections" },
];

export default function DeleteVideoCascadeModal({ video, lang = "fr", onClose, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [keepSet, setKeepSet] = useState(new Set());
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);
  const title = video?.title || video?.original_filename || video?.id || "";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/videos/${video.id}/cascade-preview`, { withCredentials: true });
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || "Impossible de charger l'aperçu de suppression");
        }
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();
    return () => { cancelled = true; };
  }, [video.id]);

  const toggleKeep = (key) => {
    setKeepSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const confirmReady = typed.trim().toLowerCase() === "effacement";

  const doDelete = async () => {
    if (!confirmReady || deleting) return;
    setDeleting(true);
    try {
      const keepTypes = Array.from(keepSet);
      const url = `${API}/api/videos/${video.id}` + (keepTypes.length ? `?keep=${keepTypes.join(",")}` : "");
      const { data } = await axios.delete(url, { withCredentials: true });
      const cascade = data?.cascade || {};
      const kept = data?.kept || {};
      const cascadeMsg = Object.entries(cascade).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k}`).join(", ");
      const keptMsg = Object.entries(kept).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k} conservé(s)`).join(", ");
      toast.success(`Vidéo "${title}" supprimée${cascadeMsg ? ` (${cascadeMsg})` : ""}${keptMsg ? ` — ${keptMsg}` : ""}`);
      onDeleted?.(video.id);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Échec de la suppression : ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const counts = preview?.knowledge_counts || {};

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="delete-video-modal-overlay"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="delete-video-modal"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-200 bg-rose-50 flex items-start justify-between gap-3 rounded-t-xl">
          <div className="flex items-start gap-2">
            <Warning size={22} weight="fill" className="text-rose-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-bold text-rose-800">
                {lang === "fr" ? "Effacement définitif d'une vidéo" : "Permanent video deletion"}
              </h2>
              <p className="text-xs text-rose-700 mt-0.5 break-all">« {title} »</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-rose-100 rounded flex-shrink-0" data-testid="delete-video-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Liste des éléments + cases à cocher de conservation */}
          <div>
            <h3 className="text-sm font-semibold text-zinc-800 mb-1">
              {lang === "fr"
                ? "Éléments qui seront effacés — cochez ce que vous voulez CONSERVER :"
                : "Items that will be erased — check what you want to KEEP:"}
            </h3>
            <p className="text-[11px] text-zinc-500 mb-2">
              {lang === "fr"
                ? "Les connaissances cochées seront conservées dans la Base de connaissances (détachées de la vidéo)."
                : "Checked knowledge will be kept in the Knowledge Base (detached from the video)."}
            </p>
            {loadingPreview ? (
              <div className="flex items-center gap-2 text-sm text-zinc-500 py-3">
                <SpinnerGap size={16} className="animate-spin" />
                {lang === "fr" ? "Chargement de l'aperçu…" : "Loading preview…"}
              </div>
            ) : (
              <ul className="space-y-1.5" data-testid="delete-video-keep-list">
                {KEEP_TYPES.map((t) => {
                  const n = counts[t.key] || 0;
                  return (
                    <li key={t.key}>
                      <label
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                          keepSet.has(t.key)
                            ? "border-teal-300 bg-teal-50 text-teal-800"
                            : "border-zinc-200 hover:bg-zinc-50 text-zinc-700"
                        } ${n === 0 ? "opacity-50" : ""}`}
                        data-testid={`delete-video-keep-${t.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={keepSet.has(t.key)}
                          onChange={() => toggleKeep(t.key)}
                          disabled={n === 0}
                          className="w-4 h-4 accent-emerald-600"
                        />
                        <span className="flex-1">{lang === "fr" ? t.labelFr : t.labelEn}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${n > 0 ? "bg-zinc-100 text-zinc-700" : "bg-zinc-50 text-zinc-400"}`}>
                          {n}
                        </span>
                        {keepSet.has(t.key) && n > 0 && (
                          <span className="text-[10px] font-bold uppercase text-teal-700">
                            {lang === "fr" ? "Conservé" : "Kept"}
                          </span>
                        )}
                      </label>
                    </li>
                  );
                })}
                <li className="flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-200 bg-zinc-50 text-sm text-zinc-500">
                  <Trash size={14} className="flex-shrink-0" />
                  <span className="flex-1">
                    {lang === "fr"
                      ? "Vidéo (fichier + métadonnées), transcript, segments et embeddings"
                      : "Video (file + metadata), transcript, segments and embeddings"}
                  </span>
                  <span className="text-[10px] font-bold uppercase text-rose-600">
                    {lang === "fr" ? "Toujours effacés" : "Always erased"}
                  </span>
                </li>
              </ul>
            )}
          </div>

          {/* Avertissement coûts */}
          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            💸 {lang === "fr"
              ? "Pour régénérer cette vidéo, il faudra refaire TOUTES les étapes du pipeline IA (transcription Whisper, diarisation AssemblyAI, fusion, extraction Q&R/Scripts/Procédures via GPT-5.2) — des coûts AI seront consommés sur votre clé BYOK."
              : "To regenerate this video, ALL AI pipeline steps must be redone (Whisper transcription, AssemblyAI diarization, merge, Q&A/Scripts/Procédures extraction via GPT-5.2) — AI costs will be charged to your BYOK key."}
          </div>

          {/* Confirmation typée */}
          <div>
            <label className="text-sm font-semibold text-zinc-800 block mb-1">
              🔐 {lang === "fr" ? "Pour confirmer, tapez exactement :" : "To confirm, type exactly:"}{" "}
              <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-rose-700">effacement</code>
            </label>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="effacement"
              className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-rose-500/40 focus:border-rose-400 outline-none"
              data-testid="delete-video-confirm-input"
              autoComplete="off"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-end gap-2 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-zinc-300 bg-white hover:bg-zinc-100 text-zinc-700 font-medium"
            data-testid="delete-video-cancel-btn"
          >
            {lang === "fr" ? "Annuler" : "Cancel"}
          </button>
          <button
            onClick={doDelete}
            disabled={!confirmReady || deleting}
            className="px-4 py-2 text-sm rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            data-testid="delete-video-confirm-btn"
          >
            {deleting ? <SpinnerGap size={15} className="animate-spin" /> : <Trash size={15} />}
            {lang === "fr"
              ? keepSet.size > 0 ? `Effacer (conserver ${keepSet.size} type${keepSet.size > 1 ? "s" : ""})` : "Effacer définitivement"
              : "Delete permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
